// src/components/Navbar.jsx
import { useState, useRef, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { canAccessDashboard, ROLE_LABELS } from "../utils/permissions";
import { Settings, LayoutDashboard, ShoppingCart, Menu, X, ChevronDown, LogOut, User } from "lucide-react";

export default function Navbar({ navRef, onOpenModal, onOpenStore, onOpenAdmin, onOpenSettings, cartCount = 0, currentPage, onGoHome, onGoCommunity, onOpenMinecraft, onOpenForum, onOpenCharity }) {
  const { authUser, logout } = useAuth();
  const [dropOpen,   setDropOpen]   = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const dropRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const h = e => { if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const closeDrawer = () => setDrawerOpen(false);

  const NAV_LINKS = currentPage === "store"
    ? [{ label: "← Back to Home", onClick: () => { onGoHome(); closeDrawer(); } }]
    : [
        { label: "Home",         onClick: () => { onGoHome(); closeDrawer(); } },
        { label: "Community",    onClick: () => { onGoCommunity?.(); closeDrawer(); } },
        { label: "Charity Fund", onClick: () => { onOpenCharity?.(); closeDrawer(); } },
        { label: "Minecraft",    onClick: () => { onOpenMinecraft?.(); closeDrawer(); } },
        { label: "Forum",        onClick: () => { onOpenForum?.(); closeDrawer(); } },
        { label: "Store",        onClick: () => { onOpenStore(); closeDrawer(); } },
      ];

  /* ── Dropdown item style ── */
  const DI = {
    width: "100%", display: "flex", alignItems: "center", gap: 10,
    padding: "8px 16px", background: "transparent", border: "none",
    color: "var(--text2)", cursor: "pointer", fontSize: 12,
    fontFamily: "'Cinzel',serif", letterSpacing: .5,
    textAlign: "left", transition: "background .12s",
  };

  return (
    <>
      <nav className="toa-nav" ref={navRef}>

        {/* LEFT — burger + logo */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button className="toa-burger" onClick={() => setDrawerOpen(o => !o)} aria-label="Menu">
            <Menu style={{ width: 20, height: 20 }} />
          </button>
          <a href="#" className="toa-logo" onClick={e => { e.preventDefault(); onGoHome(); }}>
            The Ones Above
          </a>
        </div>

        {/* CENTRE — links absolutely centred */}
        <ul className="toa-nav-links">
          {NAV_LINKS.map(({ label, href, onClick }) => (
            <li key={label}>
              <a href={href || "#"} onClick={e => { if (onClick) { e.preventDefault(); onClick(); } }}>
                {label}
              </a>
            </li>
          ))}
        </ul>

        {/* RIGHT */}
        <div className="toa-nav-right">

          {/* Cart (store page only) */}
          {currentPage === "store" && (
            <div style={{ position: "relative" }}>
              <button className="toa-btn toa-btn-ghost" style={{ padding: "6px 10px", gap: 5 }}>
                <ShoppingCart style={{ width: 14, height: 14 }} />
                <span style={{ fontSize: 12 }}>Cart</span>
              </button>
              {cartCount > 0 && (
                <span style={{
                  position: "absolute", top: -6, right: -6,
                  background: "var(--purple)", color: "#fff",
                  fontSize: 10, fontWeight: 700, fontFamily: "'Cinzel',serif",
                  width: 18, height: 18, borderRadius: "50%",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  border: "2px solid var(--bg)",
                }}>{cartCount}</span>
              )}
            </div>
          )}

          {authUser ? (
            /* ── Username dropdown (desktop) ── */
            <div ref={dropRef} style={{ position: "relative" }} className="toa-nav-user-wrap">
              <button
                className="toa-btn toa-btn-ghost toa-nav-username-btn"
                style={{ padding: "5px 10px", gap: 7, fontSize: 12 }}
                onClick={() => setDropOpen(o => !o)}
              >
                {/* Avatar */}
                <div style={{
                  width: 24, height: 24, borderRadius: "50%", overflow: "hidden",
                  background: "linear-gradient(135deg,var(--purple),#5a3d8a)",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  {authUser.profile_picture ? <img src={authUser.profile_picture} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <User style={{ width: 12, height: 12, color: "#fff" }} />}
                </div>

                {/* Username only — role stays inside the dropdown */}
                <span className="toa-nav-username" style={{ fontFamily: "'Cinzel',serif", letterSpacing: 1 }}>
                  {authUser.username}
                </span>

                <ChevronDown style={{
                  width: 12, height: 12, color: "var(--muted)",
                  transition: "transform .18s",
                  transform: dropOpen ? "rotate(180deg)" : "none",
                }} />
              </button>

              {/* Dropdown panel */}
              {dropOpen && (
                <div style={{
                  position: "absolute", top: "calc(100% + 8px)", right: 0, minWidth: 200,
                  background: "linear-gradient(160deg,#13102a,#0a0810)",
                  border: "1px solid var(--borderB)",
                  borderRadius: 10,
                  boxShadow: "0 16px 48px rgba(0,0,0,.75), 0 0 0 1px rgba(201,168,76,.05)",
                  overflow: "hidden", zIndex: 300,
                  animation: "toa-fadein .12s ease",
                }}>
                  {/* User header */}
                  <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border)" }}>
                    <div style={{ fontFamily: "'Cinzel',serif", fontSize: 12, color: "var(--text)", fontWeight: 600 }}>{authUser.username}</div>
                    <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>{ROLE_LABELS[authUser.role] || authUser.role}</div>
                  </div>

                  <div style={{ padding: "6px 0" }}>
                    {/* Settings — gear icon */}
                    <button style={DI}
                      onClick={() => { setDropOpen(false); onOpenSettings?.(); }}
                      onMouseOver={e => e.currentTarget.style.background = "rgba(255,255,255,.05)"}
                      onMouseOut={e =>  e.currentTarget.style.background = "transparent"}
                    >
                      <Settings style={{ width: 13, height: 13 }} />
                      Settings
                    </button>

                    {/* Admin Dashboard — grid/dashboard icon (different from Settings) */}
                    {canAccessDashboard(authUser.role) && (
                      <button style={{ ...DI, color: "var(--purpleB)" }}
                        onClick={() => { setDropOpen(false); onOpenAdmin?.(); }}
                        onMouseOver={e => e.currentTarget.style.background = "rgba(123,94,167,.08)"}
                        onMouseOut={e =>  e.currentTarget.style.background = "transparent"}
                      >
                        <LayoutDashboard style={{ width: 13, height: 13 }} />
                        Admin Dashboard
                      </button>
                    )}

                    <div style={{ height: 1, background: "var(--border)", margin: "4px 0" }} />

                    {/* Log Out */}
                    <button style={{ ...DI, color: "#f87171" }}
                      onClick={() => { setDropOpen(false); logout(); }}
                      onMouseOver={e => e.currentTarget.style.background = "rgba(248,113,113,.06)"}
                      onMouseOut={e =>  e.currentTarget.style.background = "transparent"}
                    >
                      <LogOut style={{ width: 13, height: 13 }} />
                      Log Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              <button className="toa-btn toa-btn-ghost" onClick={() => onOpenModal("login")}>Log In</button>
              <button className="toa-btn toa-btn-p"     onClick={() => onOpenModal("signup")}>Join Us</button>
            </>
          )}
        </div>
      </nav>

      {/* ── Mobile drawer ── */}
      {drawerOpen && <div className="toa-drawer-overlay" onClick={closeDrawer} />}
      <div className={`toa-drawer ${drawerOpen ? "toa-drawer-open" : ""}`}>
        <div className="toa-drawer-header">
          <span className="toa-logo">The Ones Above</span>
          <button className="toa-drawer-close" onClick={closeDrawer} aria-label="Close">
            <X style={{ width: 18, height: 18 }} />
          </button>
        </div>
        <nav className="toa-drawer-nav">
          {NAV_LINKS.map(({ label, href, onClick }) => (
            <a key={label} href={href || "#"} className="toa-drawer-link"
              onClick={e => { if (onClick) { e.preventDefault(); onClick(); } else closeDrawer(); }}>
              {label}
            </a>
          ))}

          {/* Settings and Admin only appear in the desktop dropdown, not here */}
        </nav>
        {/* Log In / Join Us intentionally omitted here — those buttons already
            stay visible in the top navbar on mobile, so repeating them in the
            drawer would be a duplicate. Logged-in users still get their
            username + Log Out shown below. */}
        {authUser && (
          <div className="toa-drawer-auth">
            <div className="toa-drawer-user">
              <span>{authUser.username}</span>
            </div>
            <button className="toa-btn toa-btn-ghost" style={{ width: "100%" }}
              onClick={() => { logout(); closeDrawer(); }}>
              <LogOut style={{ width: 13, height: 13 }} /> Log Out
            </button>
          </div>
        )}
      </div>
    </>
  );
}